import React from "react";
import StepShell from "./StepShell";
import { describeLoad, dollars } from "./loadFormat";
import { isOffersOnly } from "./PriceStep";

const toDollars = (cents) => (cents != null ? Math.round(cents / 100) : null);

export function DoneCheck() {
  return (
    <span
      aria-hidden="true"
      className="grid h-16 w-16 place-items-center rounded-full bg-emerald-700 text-white motion-safe:animate-bi-pop"
    >
      <svg viewBox="0 0 20 20" className="h-8 w-8">
        <path
          d="M5 10.5l3.2 3.2L15 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="16"
          className="motion-safe:animate-bi-draw"
        />
      </svg>
    </span>
  );
}

/*
 * After the price goes in. `result` is this visit's submit; on a reload it is
 * null and the booking's own fields say what happened.
 */
export default function Confirmation({ booking, result }) {
  const confirmedChr =
    result?.kind === "chr_confirmed" ||
    (!result && Boolean(booking.chr_book_confirmed));
  const resubmitted =
    result?.kind === "resubmitted" ||
    (!result &&
      !booking.chr_book_confirmed &&
      booking.chr_bidding_status === "submitting");
  const amount =
    result?.amount ??
    (confirmedChr
      ? toDollars(booking.chr_accepted_price_cents)
      : toDollars(
          booking.negotiation_amount_cents ?? booking.posted_rate_cents,
        ));
  const { origin, dest } = describeLoad(booking.load_snapshot);
  const price = amount != null ? dollars(amount) : "your price";
  const offersOnly = !confirmedChr && !resubmitted && isOffersOnly(booking);

  const title = confirmedChr
    ? `You're confirmed at ${price}`
    : resubmitted
      ? "Your new offer is in"
      : offersOnly
        ? "Your offer is in"
        : "Your price is in";
  const body = confirmedChr
    ? "We're locking it in with the broker. You'll get a text when it's done."
    : resubmitted
      ? `We sent ${price} to the broker. You'll get a text as soon as they answer.`
      : offersOnly
        ? `We sent ${price} for ${origin} → ${dest} to the broker. We'll text you when the broker answers.`
        : booking.is_parade
          ? `We sent ${price} for ${origin} → ${dest} to the broker. If they say yes, the load is yours at that price.`
          : `We sent ${price} for ${origin} → ${dest} to the broker. We'll text you as soon as they answer.`;

  return (
    <StepShell question={title} hero={<DoneCheck />}>
      <p className="m-0 font-[Roboto] text-[17px] leading-snug text-slate-700">
        {body}
      </p>
      {booking.chr_manual_takeover && (
        <p className="m-0 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 font-[Roboto] text-[15px] leading-snug text-amber-950">
          Our dispatch team is finishing this with the broker. Your reservation
          is still active, and you won't be charged unless the load is booked.
        </p>
      )}
      <ul className="m-0 flex list-disc flex-col gap-1.5 pl-5 font-[Roboto] text-[15px] text-slate-600">
        <li>
          {(result?.creditApplied ?? booking.free_booking_available)
            ? "This one's on us. You won't be charged for this load."
            : "No charge yet. Our fee only comes after the load is booked."}
        </li>
        <li>Your payment method is saved for next time.</li>
      </ul>
    </StepShell>
  );
}
